import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { APP_CONFIG, ConfigSettings } from '@shared/config';
import { ROUTES } from '@shared/config/routes.config';
import { SessionService } from '@security/session.service';
import { LocalStorageService } from '@shared/services/local-storage.service';
import { ToastrService } from '@shared/services/toaster.service';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import {
  LOGIN_ERROR_QUERY_PARAM_NAME,
  LoginErrorType,
} from 'src/app/should-login/should-login.config';
import { HttpErrorSkip } from './http-error-skip.model';
import { AUTH_URLS } from '@shared/config/url.config';
import { RefreshSessionService } from '@services/refresh-session.service';

enum ERROR_TYPE {
  CONNECT,
  WRONG_REQUEST,
  UNEXPECTED_ERROR,
}
@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  private readonly ERROR_TEXT = {
    [ERROR_TYPE.CONNECT]: 'Failed to connect ',
    [ERROR_TYPE.WRONG_REQUEST]: 'Wrong request! ',
    [ERROR_TYPE.UNEXPECTED_ERROR]: 'Unexpected error! ',
  };

  private orUrl: string;
  private dataApiUrl: string;
  private redirectUri: string;

  constructor(
    @Inject(APP_CONFIG)
    { orApiUrl, dataApiUrl, msalConfig }: ConfigSettings,
    private router: Router,
    private toastr: ToastrService,
    private readonly sessionService: SessionService,
    private refreshSessionService: RefreshSessionService
  ) {
    this.orUrl = orApiUrl;
    this.dataApiUrl = dataApiUrl;
    this.redirectUri = msalConfig.redirectUri;
  }
  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    let skipInterceptor: HttpErrorSkip;
    if (req.headers.has(HttpErrorSkip.HEADER_KEY)) {
      const skipInterceptorConfig: string = req.headers.get(
        HttpErrorSkip.HEADER_KEY
      );
      req.headers.delete(HttpErrorSkip.HEADER_KEY);
      skipInterceptor = HttpErrorSkip.fromString(skipInterceptorConfig);
    }

    return next
      .handle(req)
      .pipe(
        catchError((error: HttpErrorResponse) =>
          this.processError(error, skipInterceptor)
        )
      );
  }

  processError(error: HttpErrorResponse, skipInterceptor?: HttpErrorSkip) {
    if (
      error instanceof HttpErrorResponse &&
      !skipInterceptor?.shouldSkip(error)
    ) {
      const badRequest = error.status == 400;
      const unauthorized = error.status == 401;
      const forbidden = error.status == 403;
      const notFound = error.status == 404;
      const loadBalancerError = error.status == 464;
      const serverError = error.status == 500;

      const isAzureAuthUrl = AUTH_URLS.some(authUrl =>
        error.url?.includes(authUrl)
      );
      const isDataApiUrl = error.url?.includes(this.dataApiUrl);
      const isODBackendUrl = error.url?.includes(this.orUrl);
      const isODUrl = isODBackendUrl || isDataApiUrl;
      const atLogin = window.location.href.includes(ROUTES.login);

      if (isAzureAuthUrl) {
        let errorType: number;
        switch (error.status) {
          case 0:
            errorType = LoginErrorType.NO_CONNECTION;
            this.displayError(error, ERROR_TYPE.CONNECT);
            break;
          case HttpStatusCode.Unauthorized:
          case HttpStatusCode.Forbidden:
          case HttpStatusCode.NotFound:
          case HttpStatusCode.RequestTimeout:
          case HttpStatusCode.InternalServerError:
          case HttpStatusCode.ServiceUnavailable:
            errorType = error.status;
            break;
          default:
            errorType = LoginErrorType.UNKNOWN;
            break;
        }

        this.router.navigate([ROUTES.shouldLogin], {
          queryParams: {
            [LOGIN_ERROR_QUERY_PARAM_NAME]: errorType,
          },
        });
        return throwError(() => error);
      }

      if (!error.status) {
        this.displayError(error, ERROR_TYPE.CONNECT);
        return throwError(() => {});
      }

      if (isDataApiUrl) {
        this.displayError(error.error, ERROR_TYPE.UNEXPECTED_ERROR, false);
        return throwError(() => error);
      }

      if (unauthorized) {
        this.displayError(error, ERROR_TYPE.UNEXPECTED_ERROR);

        if (isODUrl && !atLogin) {
          return this.refreshSessionService.refreshInternalToken().pipe(
            switchMap(() => throwError(() => error)),
            catchError(() => {
              this.logout(false, error);
              return throwError(() => error);
            })
          );
        }
      }

      if (badRequest || notFound) {
        this.displayError(error, ERROR_TYPE.WRONG_REQUEST);
        return throwError(() => error);
      }

      if (serverError) {
        this.displayError(error, ERROR_TYPE.UNEXPECTED_ERROR);

        if (isODBackendUrl) {
          this.router.navigate([ROUTES.shouldLogin], {
            queryParams: {
              [LOGIN_ERROR_QUERY_PARAM_NAME]: LoginErrorType.UNKNOWN,
            },
          });
        }

        return throwError(() => error);
      }

      if (forbidden || loadBalancerError) {
        this.router.navigate([ROUTES.shouldLogin], {
          queryParams: {
            [LOGIN_ERROR_QUERY_PARAM_NAME]: LoginErrorType.FORBIDDEN_CONTENT,
          },
        });
        return throwError(() => error);
      }
    }
    return throwError(() => error);
  }

  private displayError(
    error: HttpErrorResponse,
    errorType: ERROR_TYPE,
    displayToastr: boolean = true
  ) {
    let consoleMessage = this.ERROR_TEXT[errorType];
    let toastrMessage;
    switch (errorType) {
      case ERROR_TYPE.CONNECT:
        consoleMessage += error.url;
        toastrMessage = this.toastr.MESSAGE.httpRequest.error.failedConnection;
        break;
      default:
        consoleMessage +=
          error.error && error.error.message
            ? error.error.message
            : error.message;
        toastrMessage = this.toastr.MESSAGE.httpRequest.error.wrongRequest;
        break;
    }
    console.error(consoleMessage);

    if (displayToastr) {
      this.toastr.error(toastrMessage);
    }
  }

  private logout(keepAlive: boolean, error: HttpErrorResponse) {
    // If keep alive is active, redirect.
    if (keepAlive) {
      location.replace(this.redirectUri);
      return;
    }

    // take some time to see the error in the screen
    // before going to home
    setTimeout(() => {
      LocalStorageService.cleanCookies();

      // If is Azure or OR resource, logout from microsoft account
      if (
        error.url?.includes(this.orUrl) ||
        AUTH_URLS.some(authUrl => error.url?.includes(authUrl))
      ) {
        this.sessionService.logoutFromAzure();
        return;
      }

      // Else, redirect to should login page
      this.router.navigate([ROUTES.shouldLogin], {
        queryParams: {
          [LOGIN_ERROR_QUERY_PARAM_NAME]: LoginErrorType.SESSION_EXPIRED,
        },
      });
    }, 2500);
  }
}
